import type { ReactNode } from 'react'

/**
 * A small, dependency-free Markdown-lite renderer for the chat panel's agent messages.
 *
 * The local agent's replies are plain LLM prose — bold, an occasional italic or inline code span,
 * a bullet/numbered list, maybe a fenced snippet — never a full document (that's what
 * `tiptap-markdown` is for, in the real collaborative editor). Pulling in a Markdown library (and
 * likely an HTML-sanitizer alongside it, since the text is agent-generated and never trusted) for
 * this narrow a need is the same trade the in-house `diff.ts` already made for text diffs instead
 * of the banned `diff` package (CLAUDE.md rule 10): render straight to React elements — no
 * `dangerouslySetInnerHTML` anywhere here, so there is no HTML-injection surface to sanitize in
 * the first place — and keep the surface small and auditable.
 *
 * Supported: **bold**, __bold__, *italic*, _italic_, `inline code`, [label](url) links and bare
 * http(s) URLs, fenced code blocks, "- "/"* "/"+ " and "1. " lists, and blank-line paragraphs.
 * Anything else — tables, headings, nested emphasis — renders as plain text. This is intentionally
 * NOT a CommonMark-complete parser; extend it if the agent starts routinely producing something it
 * does not yet cover.
 *
 * Links: only http:, https: and mailto: targets become an `<a>` (new tab, `rel="noopener
 * noreferrer"`); any other scheme — or a relative path such as `/v1/export-files/…`, which the chat
 * panel turns into a download button instead — stays plain text. The visible text is cut to 15
 * characters + "..." so a long SharePoint URL cannot break the answer layout; the href and the
 * hover title keep the full URL.
 */
export function renderChatMarkdown(text: string): ReactNode {
  return splitBlocks(text).map((block, i) => renderBlock(block, i))
}

type Block =
  | { kind: 'code'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'para'; lines: string[] }

const FENCE_RE = /^```\w*\s*$/
const LIST_ITEM_RE = /^\s*(?:[-*+]|\d+\.)\s+/
const ORDERED_RE = /^\s*\d+\./

/** Line-based block split: fenced code, then list runs, then blank-line-separated paragraphs. */
function splitBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]!

    if (FENCE_RE.test(line)) {
      const codeLines: string[] = []
      i += 1
      while (i < lines.length && !FENCE_RE.test(lines[i]!)) {
        codeLines.push(lines[i]!)
        i += 1
      }
      i += 1 // consume the closing fence (tolerates a missing one — EOF just ends the block)
      blocks.push({ kind: 'code', text: codeLines.join('\n') })
      continue
    }

    if (line.trim() === '') {
      i += 1 // blank line: paragraph/list separator, carries no content of its own
      continue
    }

    if (LIST_ITEM_RE.test(line)) {
      const ordered = ORDERED_RE.test(line)
      const items: string[] = []
      while (i < lines.length && LIST_ITEM_RE.test(lines[i]!)) {
        items.push(lines[i]!.replace(LIST_ITEM_RE, ''))
        i += 1
      }
      blocks.push({ kind: 'list', ordered, items })
      continue
    }

    const paraLines: string[] = []
    while (i < lines.length && lines[i]!.trim() !== '' && !LIST_ITEM_RE.test(lines[i]!) && !FENCE_RE.test(lines[i]!)) {
      paraLines.push(lines[i]!)
      i += 1
    }
    blocks.push({ kind: 'para', lines: paraLines })
  }
  return blocks
}

function renderBlock(block: Block, key: number): ReactNode {
  if (block.kind === 'code') {
    return (
      <pre className="chat-md-code" key={key}>
        <code>{block.text}</code>
      </pre>
    )
  }
  if (block.kind === 'list') {
    const items = block.items.map((item, ii) => <li key={ii}>{renderInline(item)}</li>)
    return block.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>
  }
  return (
    <p key={key}>
      {block.lines.map((line, li) => (
        // eslint-disable-next-line react/no-array-index-key -- lines within one message never reorder
        <span key={li}>
          {renderInline(line)}
          {li < block.lines.length - 1 && <br />}
        </span>
      ))}
    </p>
  )
}

type Atom =
  | { kind: 'code'; text: string }
  | { kind: 'link'; href: string; label: string }
  | { kind: 'text'; text: string }

/** Atoms, matched before any emphasis: a `code span`, a [label](url) link (its url may hold one
 *  level of balanced parentheses) and a bare http(s) URL. Leftmost wins, so a URL inside a code
 *  span stays code. */
const ATOM_RE = /`([^`]+)`|\[([^\]]+)\]\(([^\s()]+(?:\([^\s()]*\)[^\s()]*)*)\)|(https?:\/\/[^\s<>"`]+)/gi
/** The only link targets rendered as an `<a>`: `javascript:`, `data:`, relative paths… stay text. */
const SAFE_HREF_RE = /^(?:https?:\/\/|mailto:)/i
/** Sentence punctuation closing a bare URL is not part of it — nor a trailing `*`/`_`, so emphasis
 *  can wrap one (`**https://…**`). */
const URL_TRAILING_RE = /[.,;:!?'*_]$/
const LINK_TEXT_MAX = 15
/** Stands in for an atom while emphasis is matched: one opaque char, so a `_`/`*` inside a URL or a
 *  code span can never open or close emphasis, while `**[label](url)**` still bolds the link. */
const ATOM_MARK = String.fromCharCode(0)

/** One left-to-right, non-overlapping pass over the atom-masked text: bold wins over italic, and
 *  emphasis may wrap an atom but never another emphasis (a bold span's own text is never re-scanned
 *  for italics inside it). */
const EMPHASIS_RE = /\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_/g

function renderInline(line: string): ReactNode[] {
  const text = line.split(ATOM_MARK).join('') // a literal mark in the reply must not pose as an atom
  const atoms: Atom[] = []
  let masked = ''
  let last = 0
  ATOM_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = ATOM_RE.exec(text))) {
    const [raw, code, label, href, bareUrl] = m
    let atom: Atom
    if (code !== undefined) atom = { kind: 'code', text: code }
    else if (label !== undefined) {
      atom = SAFE_HREF_RE.test(href!)
        ? { kind: 'link', href: href!, label: label.trim() || href! }
        : { kind: 'text', text: raw } // unsafe scheme or relative path: verbatim, never emphasised
    } else {
      const url = trimBareUrl(bareUrl!)
      if (!/^https?:\/\/[^/]/i.test(url)) continue // e.g. a bare "https://..." placeholder
      atom = { kind: 'link', href: url, label: url }
      ATOM_RE.lastIndex = m.index + url.length // hand the trimmed punctuation back to the text
    }
    masked += text.slice(last, m.index) + ATOM_MARK
    atoms.push(atom)
    last = ATOM_RE.lastIndex
  }
  masked += text.slice(last)

  const out: ReactNode[] = []
  let key = 0
  let next = 0 // marks are met left to right, in atom order
  const expand = (s: string): ReactNode[] => {
    const nodes: ReactNode[] = []
    s.split(ATOM_MARK).forEach((part, i) => {
      if (i > 0) nodes.push(renderAtom(atoms[next++]!, key++))
      if (part) nodes.push(part)
    })
    return nodes
  }
  last = 0
  EMPHASIS_RE.lastIndex = 0
  while ((m = EMPHASIS_RE.exec(masked))) {
    if (m.index > last) out.push(...expand(masked.slice(last, m.index)))
    const [, boldStar, boldUnd, italicStar, italicUnd] = m
    const bold = boldStar ?? boldUnd
    if (bold !== undefined) out.push(<strong key={key++}>{expand(bold)}</strong>)
    else out.push(<em key={key++}>{expand((italicStar ?? italicUnd)!)}</em>)
    last = EMPHASIS_RE.lastIndex
  }
  if (last < masked.length) out.push(...expand(masked.slice(last)))
  return out
}

/** Drops trailing sentence punctuation; a `)` stays only when it closes a `(` inside the URL
 *  (`https://en.wikipedia.org/wiki/Foo_(bar)`), not the prose around it (`(see https://x.com)`). */
function trimBareUrl(url: string): string {
  const count = (s: string, c: string) => s.split(c).length - 1
  for (;;) {
    if (URL_TRAILING_RE.test(url)) url = url.slice(0, -1)
    else if (url.endsWith(')') && count(url, '(') < count(url, ')')) url = url.slice(0, -1)
    else return url
  }
}

function renderAtom(atom: Atom, key: number): ReactNode {
  if (atom.kind === 'code') return <code key={key}>{atom.text}</code>
  if (atom.kind === 'text') return atom.text
  return (
    <a key={key} href={atom.href} title={atom.href} target="_blank" rel="noopener noreferrer">
      {shortenLinkText(atom.label)}
    </a>
  )
}

/** First 15 characters + "..." — counted in code points, so an emoji is never cut in half. */
function shortenLinkText(text: string): string {
  const chars = Array.from(text)
  return chars.length > LINK_TEXT_MAX ? `${chars.slice(0, LINK_TEXT_MAX).join('')}...` : text
}
